import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Grid,
  Divider,
  alpha,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';

const MarkUnpaidDialog = ({ open, onClose, payable, onSuccess, companyId, canEdit = true }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reverseType, setReverseType] = useState('full'); // 'full' or 'partial'
  const [reverseAmount, setReverseAmount] = useState('');

  useEffect(() => {
    if (open && payable) {
      setReverseType('full');
      setReverseAmount(payable.paid_amount?.toFixed(2) || '0.00');
      setError(null);
    }
  }, [open, payable]);

  const handleReverseTypeChange = (event) => {
    const type = event.target.value;
    setReverseType(type);
    if (type === 'full') {
      setReverseAmount(payable.paid_amount?.toFixed(2) || '0.00');
    } else {
      setReverseAmount('');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const validateReversal = () => {
    const amount = parseFloat(reverseAmount);
    
    if (isNaN(amount)) {
      setError('Please enter a valid amount');
      return false;
    }
    
    if (amount <= 0) {
      setError('Amount must be greater than 0');
      return false;
    }
    
    if (amount > payable.paid_amount) {
      setError(`Amount cannot exceed the paid amount of ${formatCurrency(payable.paid_amount)}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      setError('You do not have permission to reverse payments.');
      return;
    }

    if (!validateReversal()) return;

    setLoading(true);
    setError(null);

    try {
      const amount = parseFloat(reverseAmount);
      const newPaidAmount = (payable.paid_amount || 0) - amount;
      const newRemainingAmount = payable.total_amount - newPaidAmount;
      
      let newPaymentStatus = 'unpaid';
      if (newPaidAmount >= payable.total_amount - 0.01) {
        newPaymentStatus = 'paid';
      } else if (newPaidAmount > 0) {
        newPaymentStatus = 'partial';
      }

      // Determine the correct transaction table (handle special cases)
      let transactionTable;
      if (payable.transaction_type === 'bottles') {
        transactionTable = 'bottle_transactions'; // bottles uses singular 'bottle'
      } else if (payable.transaction_type === 'labels') {
        transactionTable = 'label_transactions'; // labels uses singular 'label'
      } else if (payable.transaction_type === 'supplies') {
        transactionTable = 'supply_transactions'; // supplies uses singular 'supply'
      } else if (payable.transaction_type === 'expense') {
        transactionTable = 'expenses'; // expenses table, not expense_transactions
      } else if (payable.transaction_type === 'products') {
        transactionTable = 'product_inventory_transactions'; // products use full table name
      } else {
        transactionTable = `${payable.transaction_type}_transactions`;
      }

      // Update the transaction payment status
      const { error: updateError } = await supabase
        .from(transactionTable)
        .update({
          payment_status: newPaymentStatus,
          paid_amount: newPaidAmount
        })
        .eq('id', payable.transaction_id);

      if (updateError) throw updateError;

      // Show success and close
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error reversing payment:', err);
      setError('Failed to reverse payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!payable) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <UndoIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={400}>
              Reverse Payment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {payable.invoice_number || 'Invoice'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Payment Summary */}
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          background: alpha(theme.palette.error.main, 0.05),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`
        }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Total Invoice Amount
              </Typography>
              <Typography variant="h6" fontWeight={400}>
                {formatCurrency(payable.total_amount)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Currently Paid
              </Typography>
              <Typography variant="h6" fontWeight={400} color="success.main">
                {formatCurrency(payable.paid_amount)}
              </Typography>
              {payable.paid_amount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {((payable.paid_amount / payable.total_amount) * 100).toFixed(1)}% paid
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Current Remaining Balance
              </Typography>
              <Typography variant="h6" fontWeight={400}>
                {formatCurrency(payable.remaining_amount)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Reverse Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={400} gutterBottom>
            Reversal Type
          </Typography>
          <RadioGroup
            value={reverseType}
            onChange={handleReverseTypeChange}
          >
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={400}>
                    Full Reversal
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reverse all payments: {formatCurrency(payable.paid_amount)}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="partial"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={400}>
                    Partial Reversal
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reverse a specific amount
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {/* Reverse Amount */}
        <TextField
          label="Amount to Reverse"
          type="number"
          fullWidth
          value={reverseAmount}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || parseFloat(value) >= 0) {
              setReverseAmount(value);
              if (error && parseFloat(value) > 0 && parseFloat(value) <= payable.paid_amount) {
                setError(null);
              }
            }
          }}
          onBlur={(e) => {
            const value = parseFloat(e.target.value);
            if (value > payable.paid_amount) {
              setReverseAmount(payable.paid_amount.toFixed(2));
              setError(`Amount capped at paid amount: ${formatCurrency(payable.paid_amount)}`);
            }
          }}
          disabled={reverseType === 'full'}
          inputProps={{
            step: '0.01',
            min: '0.01',
            max: payable.paid_amount
          }}
          sx={{ mb: 2 }}
          helperText={
            reverseType === 'partial' 
              ? `Maximum: ${formatCurrency(payable.paid_amount)} (Amount currently paid)` 
              : 'Full reversal amount'
          }
        />

        {/* Preview Result */}
        {reverseAmount && parseFloat(reverseAmount) > 0 && (
          <Box sx={{ 
            p: 2, 
            background: alpha(theme.palette.info.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
          }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
              After reversal:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2">Paid Amount:</Typography>
              <Typography variant="body2" fontWeight={400} color="success.main">
                {formatCurrency((payable.paid_amount || 0) - parseFloat(reverseAmount))}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="body2">Remaining Balance:</Typography>
              <Typography variant="body2" fontWeight={400} color="error.main">
                {formatCurrency(payable.total_amount - ((payable.paid_amount || 0) - parseFloat(reverseAmount)))}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading || !canEdit}
          startIcon={<UndoIcon />}
        >
          {loading ? 'Processing...' : 'Reverse Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkUnpaidDialog;
